import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getRef = () => new URLSearchParams(window.location.search).get("ref") || null;

// Render assistant text as clean plain text with clickable links (no markdown).
const renderText = (text) => {
  const clean = (text || "").replace(/\*\*/g, "").replace(/__/g, "");
  const parts = clean.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p.replace(/[.,)]+$/, "")} target="_blank" rel="noopener noreferrer" className="text-[#B8860B] underline break-words">{p}</a>
    ) : (
      <span key={i}>{p}</span>
    )
  );
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi 👋 I'm the Magnus Kor concierge. Ask me about how we hire for startups, our engagement models, open roles, or joining as talent — how can I help?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("mk_chat_session") || null);
  const [showLead, setShowLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", company: "", intent: "hiring" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, showLead]);

  const persistSession = (id) => {
    if (id && id !== sessionId) { setSessionId(id); localStorage.setItem("mk_chat_session", id); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        ref: getRef(),
      });
      persistSession(res.data.session_id);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: res.data.reply };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble responding. Please try the contact page and we'll get right back to you." };
        return copy;
      });
    } finally {
      setSending(false);
      setMessages((m) => (m.length >= 3 && !leadSent ? (setShowLead(true), m) : m));
    }
  };

  const submitLead = async () => {
    if (!lead.name.trim() || !lead.email.trim()) return;
    try {
      const lastUser = [...messages].reverse().find((m) => m.role === "user");
      await axios.post(`${API}/chat/lead`, {
        session_id: sessionId || "no-session",
        name: lead.name,
        email: lead.email,
        company: lead.company,
        need: lastUser?.content || "",
        intent: lead.intent,
        ref: getRef(),
      });
      setLeadSent(true);
      setShowLead(false);
      const firstName = lead.name.trim().split(" ")[0];
      setMessages((m) => [...m, { role: "assistant", content: `Thank you, ${firstName}. Your details are with our team and we'll be in touch within one business day at ${lead.email}. In the meantime, feel free to explore ${lead.intent === "talent" ? "open roles at https://magnuskor.com/jobs" : "how we work at https://magnuskor.com/for-startups"} — or ask me anything else here.` }]);
    } catch (e) {
      setShowLead(true);
      setMessages((m) => [...m, { role: "assistant", content: "Hmm, that didn't save. Please try again, or reach us via https://magnuskor.com/contact" }]);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat"
        data-testid="chat-launcher"
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center shadow-xl border border-[#B8860B]/40 hover:bg-[#B8860B] transition-colors duration-300"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#1A1A1A] text-white px-5 py-4">
            <p className="font-display text-lg leading-none">Magnus Kor</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#B8860B] mt-1">Talent Concierge</p>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF9F6]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#1A1A1A] text-white" : "bg-white border border-gray-200 text-gray-700"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content ? (m.role === "assistant" ? renderText(m.content) : m.content) : (sending && i === messages.length - 1 ? <Loader2 size={16} className="animate-spin text-[#B8860B]" /> : "")}
                </div>
              </div>
            ))}

            {/* Inline lead capture */}
            {showLead && !leadSent && (
              <div className="bg-white border border-[#B8860B]/40 p-4" data-testid="chat-lead-form">
                <p className="text-sm text-gray-700 mb-3">Want the team to follow up? Leave your details:</p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setLead({ ...lead, intent: "hiring" })} className={`flex-1 text-xs py-1.5 border ${lead.intent === "hiring" ? "border-[#B8860B] text-[#B8860B]" : "border-gray-200 text-gray-500"}`} data-testid="chat-lead-hiring">I'm hiring</button>
                    <button onClick={() => setLead({ ...lead, intent: "talent" })} className={`flex-1 text-xs py-1.5 border ${lead.intent === "talent" ? "border-[#B8860B] text-[#B8860B]" : "border-gray-200 text-gray-500"}`} data-testid="chat-lead-talent">Looking for a role</button>
                  </div>
                  <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Name" className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="chat-lead-name" />
                  <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Work email" className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="chat-lead-email" />
                  <input value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} placeholder="Company (optional)" className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="chat-lead-company" />
                  <div className="flex gap-2">
                    <button onClick={submitLead} className="flex-1 bg-[#1A1A1A] text-white text-sm py-2 hover:bg-[#B8860B] transition-colors" data-testid="chat-lead-submit">Send</button>
                    <button onClick={() => setShowLead(false)} className="px-3 text-sm text-gray-400" data-testid="chat-lead-dismiss">Not now</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 flex items-center gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything…"
              className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#B8860B]"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={sending} className="w-10 h-10 flex-shrink-0 bg-[#1A1A1A] text-white flex items-center justify-center hover:bg-[#B8860B] transition-colors disabled:opacity-50" data-testid="chat-send">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
